import {useCallback, useMemo, useState} from 'react'
import type {ReviewEffortValue} from '@github-ui/copilot-code-review-controls/review-effort'
import type {CopilotReviewerRow} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {
  normalizeCopilotReviewEffortSource,
  readCopilotReviewEffortAcceptance,
  readCopilotReviewEffortSubmission,
  type CopilotReviewEffortRequest,
  type CopilotReviewEffortSource,
} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'
import {normalizeCopilotReviewEffort} from './copilot-review-effort'

export function useCopilotReviewEffortDecision(
  row: CopilotReviewerRow | undefined,
  scope = '',
  reviewState?: string,
  completedReviewId?: string,
  dataSource?: 'cached' | 'network',
) {
  const resolved = normalizeCopilotReviewEffort(row?.selectedReviewEffort)
  const resolvedSource = normalizeCopilotReviewEffortSource(row?.selectedReviewEffortSource)
  const resolvedSelected = row?.reviewEffortLevelSelected ?? false
  const seedSelection = resolvedSelected && resolved ? {effort: resolved, source: resolvedSource} : undefined
  const persistedEffort = resolvedSource === 'previous_review' ? resolved : undefined
  const [state, setState] = useState<{
    scope: string
    reviewState?: string
    completedReviewId?: string
    persistedEffort?: ReviewEffortValue
    seedSelection?: {effort: ReviewEffortValue; source?: CopilotReviewEffortSource}
    revision: number
    changedLocally: boolean
    captures: ReadonlyMap<symbol, 'pending' | 'failed'>
    awaitingInitialRefresh: boolean
    awaitingReviewChange: boolean
    decision?: {effort: ReviewEffortValue; selected: boolean; source?: CopilotReviewEffortSource}
  }>({
    scope,
    reviewState,
    completedReviewId,
    persistedEffort,
    seedSelection,
    revision: 0,
    changedLocally: false,
    captures: new Map(),
    awaitingInitialRefresh: dataSource === 'cached',
    awaitingReviewChange: false,
  })
  const decision = state.scope === scope ? state.decision : undefined
  const hasCapturedRequests = state.captures.size > 0
  const reviewChanged =
    reviewState !== undefined && state.reviewState !== undefined && reviewState !== state.reviewState
  const hasNewCompletedReview = completedReviewId !== undefined && completedReviewId !== state.completedReviewId
  // A dismissed request can change persisted intention without creating a completed review.
  const persistedEffortChanged = persistedEffort !== undefined && persistedEffort !== state.persistedEffort
  const seedSelectionChanged =
    seedSelection?.effort !== state.seedSelection?.effort || seedSelection?.source !== state.seedSelection?.source
  const hasNewStagedSelection = seedSelection !== undefined && seedSelectionChanged
  if (
    state.scope !== scope ||
    (!decision && resolved && !hasCapturedRequests) ||
    (!state.changedLocally &&
      ((state.awaitingInitialRefresh && dataSource === 'network') ||
        (persistedEffortChanged &&
          (!state.awaitingReviewChange ||
            state.persistedEffort !== undefined ||
            persistedEffort !== decision?.effort)) ||
        (hasNewStagedSelection && !hasCapturedRequests && !state.awaitingReviewChange) ||
        (reviewChanged && (!state.awaitingReviewChange || hasNewCompletedReview))))
  ) {
    setState({
      scope,
      reviewState,
      completedReviewId,
      persistedEffort,
      seedSelection,
      revision: state.revision + 1,
      changedLocally: false,
      captures: new Map(),
      awaitingInitialRefresh: dataSource === 'cached',
      awaitingReviewChange: false,
      decision: resolved ? {effort: resolved, selected: resolvedSelected, source: resolvedSource} : undefined,
    })
  } else if (
    reviewState !== undefined &&
    (reviewState !== state.reviewState || persistedEffort !== state.persistedEffort || seedSelectionChanged)
  ) {
    // Tracking a review change must not invalidate a still-current local request.
    setState({
      ...state,
      reviewState,
      completedReviewId,
      persistedEffort,
      seedSelection,
      awaitingReviewChange: state.awaitingReviewChange && reviewState === state.reviewState,
    })
  }

  const reviewerId = row?.reviewer ? Number(row.reviewer.id) : undefined
  const shownEffort = decision?.effort ?? (hasCapturedRequests ? undefined : resolved)
  const explicitEffort = decision?.selected ? decision.effort : undefined
  const canCaptureRequest = Boolean(row?.requestable && row.reviewEffortOptions.length > 0)
  const stageEffort = useCallback(
    (value: ReviewEffortValue) =>
      setState(current =>
        current.scope === scope && current.decision?.effort !== value
          ? {
              ...current,
              revision: current.revision + 1,
              changedLocally: true,
              captures: new Map(),
              awaitingInitialRefresh: false,
              awaitingReviewChange: false,
              decision: {effort: value, selected: true, source: 'explicit_selection'},
            }
          : current,
      ),
    [scope],
  )
  const captureRequest = useCallback((): CopilotReviewEffortRequest | undefined => {
    if (!canCaptureRequest) return
    const token = Symbol()
    setState(current =>
      current.scope === scope && current.revision === state.revision
        ? {...current, captures: new Map(current.captures).set(token, 'pending'), awaitingInitialRefresh: false}
        : current,
    )
    const releaseCapture = () =>
      setState(current =>
        current.scope === scope && current.revision === state.revision && current.captures.has(token)
          ? {
              ...current,
              captures: new Map([...current.captures].filter(([id, status]) => id !== token && status !== 'failed')),
            }
          : current,
      )
    return {
      fields: decision
        ? {
            review_effort_client_version: '2',
            review_effort_level: decision.effort,
            review_effort_level_selected: decision.selected,
            ...(decision.source ? {review_effort_source: decision.source} : {}),
          }
        : {review_effort_client_version: '2', review_effort_level_selected: false},
      fail: () =>
        setState(current =>
          current.scope === scope && current.revision === state.revision && current.captures.get(token) === 'pending'
            ? {...current, captures: new Map(current.captures).set(token, 'failed')}
            : current,
        ),
      dismissFailure: () =>
        setState(current => {
          if (
            current.scope !== scope ||
            current.revision !== state.revision ||
            current.captures.get(token) !== 'failed'
          ) {
            return current
          }
          const captures = new Map(current.captures)
          captures.delete(token)
          return {...current, captures}
        }),
      acceptResponse: response => {
        let submitted
        let accepted
        try {
          submitted = readCopilotReviewEffortSubmission(response)
          accepted = submitted ? undefined : readCopilotReviewEffortAcceptance(response)
        } catch (error) {
          releaseCapture()
          throw error
        }
        if (submitted) {
          const handled = submitted
          setState(current =>
            current.scope === scope && current.revision === state.revision && current.captures.has(token)
              ? {
                  ...current,
                  revision: current.revision + 1,
                  changedLocally: false,
                  captures: new Map(),
                  awaitingInitialRefresh: false,
                  awaitingReviewChange: current.reviewState === state.reviewState,
                  decision: current.decision
                    ? {...current.decision, selected: false}
                    : {
                        effort: handled.review_effort_level,
                        selected: false,
                        source: handled.review_effort_source,
                      },
                }
              : current,
          )
          return
        }
        const acceptance = accepted
        const value = normalizeCopilotReviewEffort(acceptance?.review_effort_level)
        if (!value) {
          releaseCapture()
          return
        }
        setState(current =>
          current.scope === scope && current.revision === state.revision && current.captures.has(token)
            ? {
                ...current,
                revision: current.revision + 1,
                changedLocally: false,
                captures: new Map(),
                awaitingInitialRefresh: false,
                awaitingReviewChange: current.reviewState === state.reviewState,
                decision: {effort: value, selected: false, source: acceptance?.review_effort_source},
              }
            : current,
        )
      },
    }
  }, [canCaptureRequest, decision, scope, state])

  return useMemo(
    () => ({
      reviewerId,
      shownEffort,
      explicitEffort,
      stageEffort,
      captureRequest,
    }),
    [captureRequest, explicitEffort, reviewerId, shownEffort, stageEffort],
  )
}
